// Internal-tabs data model. See fund-valuation-infra/plans/internal-tabs.md
// §3.1 for the design this mirrors.

/** A frozen snapshot of a react-router location, just the 3 fields we need. */
export interface LopTabLocation {
  pathname: string;
  search: string;
  hash: string;
}

export interface LopTab {
  /** tabKey(location) — pathname + sorted identity-param allowlist (D4). */
  key: string;
  location: LopTabLocation;
  /** Page displayName (+ " · #id" for identity params) unless overridden. */
  title: string;
  /** ICON_MAP key, resolved from Pages.iconName by url===pathname. */
  iconName: string | null;
  /** The home tab ("/") — always present, cannot be closed. */
  pinned: boolean;
  /** Set by useTabDirty (plan §3.6) — drives the tab-bar dirty dot and the
   *  close/logout/beforeunload confirmations. */
  dirty: boolean;
  /** True once a page has called useTabTitle for this tab (plan §3.6) — tells
   *  the reducer's router-driven paths (NAV_OPEN_OR_FOCUS re-focusing an
   *  already-open tab) to keep the page's own title/icon instead of
   *  overwriting them with the router-derived default every time the tab is
   *  merely re-focused. Reset to false only when the tab's IDENTITY itself
   *  changes (NAV_REPLACE mutate-in-place). */
  titleOverride?: boolean;
  openedAt: number;
}

export interface TabsState {
  tabs: LopTab[];
  activeKey: string;
  /** Most-recently-used key order; mru[0] is always the active key. Reserved
   *  for the Phase-2 Alt+Q MRU switcher — maintained from Phase 1 onward so
   *  that feature needs no reducer change. */
  mru: string[];
  /** Set whenever a navigation wanted to open a genuinely NEW tab but the
   *  MAX_TABS cap blocked it (D5: no auto-close, just tell the user) — the
   *  location it was trying to reach. Non-null while `LopTabsCapModal` is
   *  open; cleared (CAP_DISMISS) on ביטול/Escape/backdrop or after אישור
   *  performs the pending navigation. */
  capBlocked: LopTabLocation | null;
}

/** Hard cap on open tabs (decision D5 — BLOCK, not overwrite: a navigation
 *  that would need a brand-new tab slot while already at the cap is refused
 *  outright. No tab is added, changed, or closed; see reducer.ts and
 *  TabRouterSync.ts). Includes the pinned home tab. */
export const MAX_TABS = 12;

/** Identity-param allowlist (decision D4) — sorted before use so param order
 *  in the URL never produces two different keys for the same identity. */
export const IDENTITY_PARAMS: readonly string[] = ['id'];

export const HOME_PATHNAME = '/';
